import { betrag } from './api'
import { Auswahlfeld } from './Auswahlfeld'
import { Kontofeld } from './Kontofeld'
import { ohneStatistikkonten, ohneTechnische } from './Festkonten'
import { anzahlwort, bewegungsartname, katalog, kontobezeichnung, t } from './sprache'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { vorratsname } from './Datenquellen'
import type { Bewegungsartvorschlag } from './Datenquellen'
import type { Vorratswert, Vorschlagsrichtung, Vorschlagstreffer } from './typen'

/**
 * Die Datenquelle schlägt Bewegungsarten vor (5.1 Abschnitt 3.35, R871–R874).
 *
 * Zwei Orte, eine Sache: An der **Datenquelle** steht, was vorgeschlagen wird —
 * *Abnahme auf 2200 Gewinnrücklagen als Ausschüttung* —, und in der
 * **Vorschau eines Imports** wird gefragt, ob es diesmal gilt.
 */

const RICHTUNGEN: Vorschlagsrichtung[] = ['abnahme', 'zunahme']

function richtungswort(r: Vorschlagsrichtung): string {
  return r === 'zunahme' ? t('quelle.vorschlaege.zunahme') : t('quelle.vorschlaege.abnahme')
}

/**
 * Der Abschnitt im Blatt der Datenquelle: je Vorschlag eine Zeile mit drei
 * Auswahlfeldern, kein Freitext (R871).
 *
 * **Gespeichert wird mit der Quelle** — über denselben Knopf wie die
 * Spaltenzuordnung, weil beides eine Fassung der Quelle ist (R521).
 */
export function Vorschlagsliste({
  vorschlaege,
  bewegungsarten,
  festeBewegungsart,
  laeuft,
  aufAendern,
}: {
  vorschlaege: Bewegungsartvorschlag[]
  /** Der Vorrat aus dem Zielfeld Bewegungsart — derselbe wie an der Spiegelspalte. */
  bewegungsarten: Vorratswert[]
  /** Ob die Bewegungsart an der Quelle fest steht — dann greift kein Vorschlag (R872). */
  festeBewegungsart: boolean
  laeuft: boolean
  aufAendern: (vorschlaege: Bewegungsartvorschlag[]) => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415).
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('datenquelle:vorschlaege')

  // Die technischen Konten liefert keine Gesellschaft (R1427).
  // Ohne Statistikkonten: Sie tragen keine Bewegungsart (R1729, R1734).
  const konten = ohneStatistikkonten(ohneTechnische([...katalog().konten.values()], (k) => k.schluessel), (k) => k.schluessel)
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
    .map((k) => ({ wert: k.schluessel, text: kontobezeichnung(k.schluessel), suchtext: k.code, stillgelegtAb: k.stillgelegtAb }))
  const arten = bewegungsarten.map((w) => ({
    wert: w.schluessel,
    text: vorratsname(w),
    suchtext: w.schluessel,
    stillgelegtAb: w.stillgelegtAb,
  }))

  function setze(i: number, teil: Partial<Bewegungsartvorschlag>) {
    aufAendern(vorschlaege.map((v, j) => (j === i ? { ...v, ...teil } : v)))
  }

  return (
    <>
      {/* Der eine Satz, der nicht selbstverständlich ist (R144): warum hier nichts greift. */}
      {festeBewegungsart && vorschlaege.length > 0 && <p className="hinweis">{t('quelle.vorschlaege.fest')}</p>}

      {vorschlaege.length > 0 && (
        <div className="tabellenrahmen">
          <table ref={spaltenrahmen} className="ziehbaretabelle vorschlagstabelle">
            <Spaltenmassleiste masse={spaltenmasse} />
            <thead>
              <tr>
                <th>{t('quelle.vorschlaege.konto')}</th>
                <th>{t('quelle.vorschlaege.richtung')}</th>
                <th>{t('quelle.vorschlaege.bewegungsart')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vorschlaege.map((v, i) => (
                <tr key={i}>
                  <td>
                    <Kontofeld
                      wert={v.konto}
                      beschriftung={t('quelle.vorschlaege.konto')}
                      leer="—"
                      eintraege={konten}
                      abgeschaltet={laeuft}
                      aufWahl={(konto) => setze(i, { konto })}
                    />
                  </td>
                  <td>
                    <select
                      aria-label={t('quelle.vorschlaege.richtung')}
                      value={v.richtung}
                      disabled={laeuft}
                      onChange={(e) => setze(i, { richtung: e.target.value as Vorschlagsrichtung })}
                    >
                      {RICHTUNGEN.map((r) => (
                        <option key={r} value={r}>
                          {richtungswort(r)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <Auswahlfeld
                      sorte="verweis"
                      wert={v.bewegungsart}
                      beschriftung={t('quelle.vorschlaege.bewegungsart')}
                      leer="—"
                      eintraege={arten}
                      abgeschaltet={laeuft}
                      aufWahl={(bewegungsart) => setze(i, { bewegungsart })}
                    />
                  </td>
                  <td className="handgriff">
                    <button
                      type="button"
                      className="schmal"
                      title={t('quelle.vorschlaege.entfernen')}
                      aria-label={t('quelle.vorschlaege.entfernen')}
                      disabled={laeuft}
                      onClick={() => aufAendern(vorschlaege.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="handreichung">
        <button
          type="button"
          disabled={laeuft}
          onClick={() => aufAendern([...vorschlaege, { konto: '', richtung: 'abnahme', bewegungsart: '' }])}
        >
          {t('quelle.vorschlaege.neu')}
        </button>
      </p>
    </>
  )
}

/** Was zugeklappt vom Abschnitt übrig bleibt — die Zahl der Vorschläge (R526). */
export function vorschlagszusatz(anzahl: number): string {
  return anzahl === 0
    ? t('quelle.vorschlaege.zusatz.keine')
    : anzahl === 1
      ? t('quelle.vorschlaege.zusatz.eins')
      : t('quelle.vorschlaege.zusatz.mehrere', { anzahl: anzahlwort(anzahl) })
}

/** Ein Vorschlag als Satz — *Abnahme auf 2200 Gewinnrücklagen als Ausschüttung*. */
export function vorschlagssatz(v: Pick<Vorschlagstreffer, 'konto' | 'richtung' | 'bewegungsart'>): string {
  return t('import.vorschlag.satz', {
    richtung: richtungswort(v.richtung),
    konto: kontobezeichnung(v.konto),
    bewegungsart: bewegungsartname(v.bewegungsart),
  })
}

/**
 * Legt die Antworten einer früheren Vorschau auf eine neu gelesene — erkannt an
 * Konto und Richtung, die zusammen nur einmal vorkommen (R871). Ein Vorschlag,
 * der neu dazukommt, bleibt unbeantwortet.
 */
export function mitBisherigenAntworten<V extends { vorschlaege?: Vorschlagstreffer[] | null }>(
  neu: V,
  bisher: V | null,
): V {
  if (!neu.vorschlaege || !bisher?.vorschlaege) {
    return neu
  }

  return {
    ...neu,
    vorschlaege: neu.vorschlaege.map((v) => {
      const alt = bisher.vorschlaege!.find((a) => a.konto === v.konto && a.richtung === v.richtung)
      return alt && alt.angenommen !== null ? { ...v, angenommen: alt.angenommen } : v
    }),
  }
}

/** Ob jeder Vorschlag eine Antwort hat — vorher wird nicht übernommen (R873). */
export function alleBeantwortet(vorschlaege: Pick<Vorschlagstreffer, 'angenommen'>[] | null | undefined): boolean {
  return (vorschlaege ?? []).every((v) => v.angenommen !== null)
}

/**
 * Die Frage in der Vorschau: je Vorschlag ein Satz, was er trifft, und zwei
 * Knöpfe (R873).
 *
 * **Vorbelegt ist nichts.** Ein Vorschlag, der nur angezeigt wird, wäre einer,
 * den man übersieht — deshalb sperrt der Knopf *Übernehmen*, bis jede Zeile
 * eine Antwort hat, und sagt warum.
 */
export function Vorschlagsfrage({
  vorschlaege,
  mitUmfang,
  laeuft,
  aufAntwort,
}: {
  vorschlaege: Pick<Vorschlagstreffer, 'konto' | 'richtung' | 'bewegungsart' | 'zeilen' | 'betrag' | 'angenommen'>[]
  /**
   * Ob Zeilen und Betrag dastehen. **Im Stapel nicht:** Er liest vor dem Buchen
   * jeden Monat gegen den Stand vor seinem ersten (R749) — welche Bewegung ein
   * Vorschlag trifft, steht erst beim Buchen fest, und eine Zahl, die dann
   * nicht stimmt, ist schlechter als keine (R145).
   */
  mitUmfang: boolean
  laeuft: boolean
  aufAntwort: (index: number, angenommen: boolean) => void
}) {
  if (vorschlaege.length === 0) {
    return null
  }

  return (
    <div className="vorschlagsfrage">
      <h3>
        {vorschlaege.length === 1
          ? t('import.vorschlag.titel.eins')
          : t('import.vorschlag.titel.mehrere', { anzahl: anzahlwort(vorschlaege.length) })}
      </h3>
      <ul>
        {vorschlaege.map((v, i) => {
          const umfang = !mitUmfang
            ? null
            : v.zeilen === 1
              ? t('import.vorschlag.zeilen.eins', { betrag: betrag(v.betrag) })
              : t('import.vorschlag.zeilen.mehrere', { anzahl: anzahlwort(v.zeilen), betrag: betrag(v.betrag) })

          return (
            <li key={`${v.konto}|${v.richtung}`}>
              <span className="vorschlagssatz">{vorschlagssatz(v)}</span>
              {umfang && <span className="gedaempft">{umfang}</span>}
              <span className="sichtwahl">
                <button
                  type="button"
                  className={v.angenommen === true ? 'gewaehlt' : undefined}
                  aria-pressed={v.angenommen === true}
                  disabled={laeuft}
                  onClick={() => aufAntwort(i, true)}
                >
                  {t('import.vorschlag.annehmen')}
                </button>
                <button
                  type="button"
                  className={v.angenommen === false ? 'gewaehlt' : undefined}
                  aria-pressed={v.angenommen === false}
                  disabled={laeuft}
                  onClick={() => aufAntwort(i, false)}
                >
                  {t('import.vorschlag.ablehnen')}
                </button>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
